import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["bubble", "fullscreen", "unreadBadge"]
  static values = {
    expanded: { type: Boolean, default: false },
    unreadCount: { type: Number, default: 0 }
  }

  connect() {
    // Start in collapsed state
    this.showCollapsed()

    // Listen for Turbo Stream renders to scroll after message insertion
    this.boundAfterRenderHandler = this.handleAfterRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundAfterRenderHandler)

    // Also listen for form submissions to scroll after sending
    this.boundFormSubmitHandler = this.handleFormSubmit.bind(this)
    this.element.addEventListener('turbo:submit-end', this.boundFormSubmitHandler)

    // Listen for Escape key to close chat
    this.boundKeyHandler = this.handleKeyPress.bind(this)
    document.addEventListener('keydown', this.boundKeyHandler)
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.boundAfterRenderHandler)
    this.element.removeEventListener('turbo:submit-end', this.boundFormSubmitHandler)
    document.removeEventListener('keydown', this.boundKeyHandler)
  }

  toggle() {
    if (this.expandedValue) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  expand() {
    this.expandedValue = true
    this.showExpanded()
    // Reset unread count when opening
    this.unreadCountValue = 0
    this.updateUnreadBadge()

    // Scroll to bottom of messages
    this.scrollToBottom()
  }

  collapse() {
    this.expandedValue = false
    this.showCollapsed()
  }

  showCollapsed() {
    if (this.hasBubbleTarget) {
      this.bubbleTarget.classList.remove('hidden')
    }
    if (this.hasFullscreenTarget) {
      this.fullscreenTarget.classList.add('hidden')
    }
    document.body.style.overflow = ''
  }

  showExpanded() {
    if (this.hasBubbleTarget) {
      this.bubbleTarget.classList.add('hidden')
    }
    if (this.hasFullscreenTarget) {
      this.fullscreenTarget.classList.remove('hidden')
      // Add animation class
      this.fullscreenTarget.classList.add('floating-chat-fade-in')
    }
    // Prevent background scrolling when chat is open
    document.body.style.overflow = 'hidden'
  }

  handleAfterRender(event) {
    // Scroll to bottom whenever a Turbo Stream renders and chat is open
    if (this.expandedValue) {
      // Use a longer delay to ensure the DOM has updated
      setTimeout(() => this.scrollToBottom(), 0)
    }
  }

  handleFormSubmit(event) {
    // Scroll to bottom after form submission (message sent)
    if (this.expandedValue && event.detail.success) {
      setTimeout(() => this.scrollToBottom(), 0)
    }
  }

  handleKeyPress(event) {
    // Close chat when Escape is pressed and chat is open
    if (event.key === 'Escape' && this.expandedValue) {
      this.collapse()
    }
  }

  updateUnreadBadge() {
    if (!this.hasUnreadBadgeTarget) return

    if (this.unreadCountValue > 0) {
      this.unreadBadgeTarget.textContent = this.unreadCountValue > 99 ? '99+' : this.unreadCountValue
      this.unreadBadgeTarget.classList.remove('hidden')
    } else {
      this.unreadBadgeTarget.classList.add('hidden')
    }
  }

  scrollToBottom() {
    // Wait for DOM to update, then scroll
    setTimeout(() => {
      const messagesContainer = this.element.querySelector('#chat_messages')
      if (messagesContainer) {
        // Try scrolling the parent container (the one with overflow-y-auto)
        const scrollableParent = messagesContainer.closest('.overflow-y-auto')
        if (scrollableParent) {
          scrollableParent.scrollTop = scrollableParent.scrollHeight
        } else {
          messagesContainer.scrollTop = messagesContainer.scrollHeight
        }
      }
    }, 100)
  }
}
